import { useEffect, useRef, useState } from 'react';
import {
  PROJECT_CREATE_STATUSES,
  PROJECT_STATUSES,
  DEFAULT_PROJECT_COLOR,
  type Client,
  type Metafield,
  type Project,
  type ProjectStatus,
} from '@forecastify/shared';
import { Button, CheckboxField, Dialog, Select, Textarea, TextInput } from '@forecastify/ui';
import { MetafieldSection } from '../metafields/metafield-section';
import { useT, type MessageKey } from '../../i18n';
import { Field } from '../card';
import { ColorPicker } from './color-picker';
import { ClientDialog, type ClientDraft } from './client-dialog';
import { DateField } from '../date-field';
import { useOnOpen } from '../use-on-open';

/**
 * The Projekt dialog — `markup.html` 2637–2704, transcribed.
 *
 * ⚠️⚠️ **The key prefix is disabled on edit, and the dialog SAYS WHY.** The mockup's own
 * description carries the sentence — *"A kulcs-prefix nem módosítható — a meglévő feladatkulcsok
 * arra hivatkoznak."* — and it is the reason, not a warning. A disabled field with no explanation
 * reads as a bug; with one, it reads as a rule.
 *
 * The rule itself is held elsewhere and in two places: `updateProjectRequestSchema` has no field for
 * it, and migration 0014's trigger refuses the write. This control is why nobody tries.
 */
export interface ProjectDraft {
  keyPrefix: string;
  name: string;
  clientId: string;
  /**
   * ⚠️ 21 Oct 2026 · **an internal project is a box ticked, not a client picked.** It was the
   * client list's first option, an empty value called *Belső projekt* — so "no client chosen yet"
   * and "internal" were the same state, and a project saved without a client became internal by
   * omission. Ticked, `clientId` is `''` and the picker is disabled; unticked, a client is
   * required. On the wire it is still `clientId: null` — the column has always been the fact.
   */
  internal: boolean;
  status: ProjectStatus;
  /**
   * 3 Nov 2026 · why the status changed — asked for, and sent, only when it did. Empty otherwise;
   * the request leaves it out, and the server needs none for an edit that keeps the status.
   */
  statusReason: string;
  pmMemberId: string;
  code: string;
  startsOn: string;
  endsOn: string;
  color: string;
  /**
   * C10's values, keyed by the field's own key.
   *
   * ⚠️ Sent only when the workspace has defined fields for this entity — an empty object CLEARS
   * every value, so an absent one is what "leave them alone" looks like on the wire.
   */
  metafields: Record<string, unknown>;
}

/** A person who can be picked as PM. The screen supplies them; this knows nothing about members. */
export interface PickableMember {
  readonly id: string;
  readonly name: string;
  /**
   * U-10 · whether they log time. ⚠️ **Read by the project's member picker only** — a PM need not
   * log time, so this dialog ignores it. Absent is treated as *logs time*.
   */
  readonly tracksTime?: boolean;
}

export function ProjectDialog({
  open,
  editing,
  clients,
  onCreateClient,
  clientMetafields = [],
  members,
  pmCandidates,
  currentMemberId = null,
  onClose,
  onSubmit,
  metafields = [],
  serverError,
}: {
  open: boolean;
  editing: Project | null;
  readonly clients: readonly Client[];
  readonly members: readonly PickableMember[];
  /**
   * 3 Nov 2026 · **who may be named PM** — `GET /members?can=project.manage`. Absent, the PM
   * field offers `members`, as before. Editing keeps the current PM visible even when they are
   * no longer eligible, so opening the form never silently drops them.
   */
  readonly pmCandidates?: readonly PickableMember[];
  /** On CREATE, the PM field defaults to this member when they are a candidate. */
  readonly currentMemberId?: string | null;
  onClose: () => void;
  onSubmit: (draft: ProjectDraft) => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️ ITEM 21 · a client made without leaving this form.                 │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **RESOLVES TO THE NEW CLIENT'S ID, AND THAT IS THE WHOLE CONTRACT.** The picker selects
   * it on the way back, so the flow ends where it started with one more answer filled in. A
   * `void` return would leave the person to find their new client in a list that has not
   * reloaded yet.
   *
   * ⚠️ **`null` when it was refused**, so the quick dialog can stay open with its error rather
   * than closing over a client that does not exist.
   *
   * ⚠️ Absent hides the option entirely — a caller with nowhere to create one should not offer to.
   */
  readonly onCreateClient?: (draft: ClientDraft) => Promise<string | null>;
  /** The workspace's `client` metafields, for the client form opened from here. */
  readonly clientMetafields?: readonly Metafield[];
  /** The workspace's `project` metafields. Empty when none are defined — see `MetafieldSection`. */
  readonly metafields?: readonly Metafield[];
  serverError: string | null;
}) {
  const t = useT();
  const [draft, setDraft] = useState<ProjectDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  /** ⚠️ Item 21 · the quick-create client dialog, over this one. */
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientError, setNewClientError] = useState<string | null>(null);

  /* ⚠️ On OPENING only (R07, 3 Nov 2026): the project screen passes its live `project`, a new
     object on every refresh — a reset keyed on it threw away an edit in progress. */
  /* 3 Nov 2026 · a new project is run by whoever creates it, when they may run one. */
  const selfPm =
    currentMemberId !== null &&
    (pmCandidates ?? members).some((member) => member.id === currentMemberId)
      ? currentMemberId
      : '';
  /** Whether the PM was chosen by hand since opening — then the default never overrides it. */
  const pmChosen = useRef(false);

  useOnOpen(open, () => {
    setError(null);
    pmChosen.current = false;
    setDraft(
      editing === null
        ? { ...emptyDraft(), pmMemberId: selfPm }
        : {
            keyPrefix: editing.keyPrefix,
            name: editing.name,
            clientId: editing.clientId ?? '',
            internal: editing.clientId === null,
            status: editing.status,
            statusReason: '',
            pmMemberId: editing.pmMemberId ?? '',
            code: editing.code ?? '',
            startsOn: editing.startsOn ?? '',
            endsOn: editing.endsOn ?? '',
            color: editing.color,
            metafields: editing.metafields,
          },
    );
  });

  /**
   * ⚠️ 4 Nov 2026 · **THE DEFAULT ARRIVES WITH THE LIST, NOT ONLY WITH THE OPENING.** The sidebar's
   * *+* on another page lands on the projects list with the dialog ALREADY open — before the
   * eligible people have loaded, so the opening found nobody to default to and left it empty.
   */
  useEffect(() => {
    if (!open || editing !== null || pmChosen.current || selfPm === '') return;
    setDraft((current) =>
      current.pmMemberId === '' ? { ...current, pmMemberId: selfPm } : current,
    );
  }, [open, editing, selfPm]);

  const patch = (part: Partial<ProjectDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  /* 3 Nov 2026 · the eligible people, plus the standing PM of the project being edited. */
  const pmOptions = (() => {
    const eligible = pmCandidates ?? members;
    const standing = editing?.pmMemberId ?? null;
    if (standing === null || eligible.some((member) => member.id === standing)) return eligible;
    const known = members.find((member) => member.id === standing);
    const name = known?.name ?? editing?.pmName ?? null;
    return name === null ? eligible : [{ id: standing, name }, ...eligible];
  })();

  /* 3 Nov 2026 · an edit that moves the status asks why — a create has no status to move from. */
  const statusMoves = editing !== null && draft.status !== editing.status;

  const submit = (): void => {
    if (draft.name.trim().length === 0) {
      setError(t('projects.error.name'));
      return;
    }
    if (editing === null && !/^[A-Z]{2,6}$/.test(draft.keyPrefix)) {
      setError(t('projects.error.prefix'));
      return;
    }
    if (!draft.internal && draft.clientId === '') {
      setError(t('projects.error.client'));
      return;
    }
    /* ⚠️ String comparison, and it is a real date comparison because these are wire dates. It is
     * wrong for every other date format — one more reason nothing else crosses a boundary here. */
    if (draft.startsOn !== '' && draft.endsOn !== '' && draft.endsOn < draft.startsOn) {
      setError(t('projects.error.dates'));
      return;
    }
    if (statusMoves && draft.statusReason.trim() === '') {
      setError(t('projects.statusReason.required'));
      return;
    }
    onSubmit(statusMoves ? draft : { ...draft, statusReason: '' });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={editing === null ? t('projects.dialog.new') : t('projects.dialog.edit')}
      description={editing === null ? t('projects.dialog.newHint') : t('projects.dialog.editHint')}
      closeLabel={t('common.close')}
      /**
       * ⚠️⚠️ **PINNED, BECAUSE THIS IS THE ONE DIALOG LONG ENOUGH TO SCROLL.** The shell caps its
       * own height now and the body scrolls, so Save was reachable either way — but on a form with a
       * metafield section and eight fields, an action row that scrolls with the content is one
       * somebody hunts for. The `footer` slot exists for exactly this.
       *
       * ⚠️ **The error moves up here with them.** A validation refusal rendered at the bottom of a
       * scrollable body is a refusal nobody sees: you press Save, nothing appears to happen, and the
       * sentence explaining why is below the fold.
       */
      footer={
        <>
          <div className="min-w-0 flex-1">
            {error === null && serverError === null ? null : (
              <div className="text-destructive text-[12px]" role="alert">
                {error ?? serverError}
              </div>
            )}
          </div>
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {editing === null ? t('projects.action.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* ⚠️⚠️ **THE SAME TWO-COLUMN GRID AS THE BLOCK BELOW — item 21's *"boxes aligned"*.** This
            was `minmax(0,1fr)_120px` while the fields under it were `grid-cols-2`, so the prefix
            box ended 120px from the right and every field beneath it ended at the halfway line:
            three different right edges down one small dialog. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field required label={t('projects.field.name')}>
            <TextInput
              value={draft.name}
              onChange={(event) => {
                patch({ name: event.target.value });
              }}
              /* ⚠️ **Item 21 · a generic placeholder.** It read `Rossmann újratervezés` — a real
                 customer's name in an empty field, which somebody eventually submits. */
              placeholder={t('projects.field.namePlaceholderGeneric')}
            />
          </Field>
          {/* ⚠️⚠️ **THE FORMAT NOTE IS GONE — item 21's *"labels and hints removed"*.** The field
              accepts two to six letters and enforces it as you type; a sentence saying so under it
              described a rule the control already applies. Why it is LOCKED on edit stays in the
              dialog's description, which is where the mockup puts it.

              ⚠️⚠️ **THE LABELS STAY, AND THAT IS A DELIBERATE READING OF THE ITEM.** `Field` wraps
              a real `<label>`; a placeholder is not one — it vanishes on focus, is not reliably
              announced, and leaves a filled-in form with no way to tell what each box is. What the
              feedback is looking at is the second line under each field, and that is what goes. */}
          <Field required label={t('projects.field.prefix')}>
            <TextInput
              value={draft.keyPrefix}
              onChange={(event) => {
                /**
                 * ⚠️ **Letters only, and the mockup's own filter admits digits.** `setPfId` strips
                 * to `[^A-Z0-9]` while `pfSubmit` then demands `^[A-Z]{2,6}$` — so `ROSS2` can be
                 * typed and never saved. That is a defect in the prototype, and reproducing it
                 * would build a control that accepts what it will later refuse.
                 */
                patch({
                  keyPrefix: event.target.value
                    .toUpperCase()
                    .replace(/[^A-Z]/g, '')
                    .slice(0, 6),
                });
              }}
              placeholder={t('common.prefixPlaceholder')}
              className="uppercase"
              disabled={editing !== null}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Field label={t('projects.field.client')}>
              <Select
                value={draft.clientId}
                disabled={draft.internal}
                /* ⚠️ Item 21 · searchable. A workspace's client list is the long kind, and `Select`
                 draws the line at ten. */
                searchable
                searchPlaceholder={t('projects.field.client')}
                /* ⚠️ **No sentinel any more.** The quick-create was an option called `__new_client__`
                 that this handler had to intercept before it could reach the draft; `Select`'s
                 `action` slot is a control rather than a value, so there is nothing to intercept
                 and no non-uuid that could escape into `clientId`. */
                onChange={(event) => {
                  patch({ clientId: event.target.value });
                }}
                /* ⚠️ Archived clients are OUT of this list, matching `pfClientOptions` — and the
                 * server refuses to archive a client with live projects for exactly this reason: a
                 * project whose client cannot be re-selected after any edit. */
                options={[
                  /* "Nothing chosen" — `Select` has no placeholder, by design. Not *internal* any
                   more: that is the box below. */
                  {
                    value: '',
                    label: draft.internal
                      ? t('projects.field.noClient')
                      : t('projects.field.pickClient'),
                  },
                  ...clients
                    .filter((client) => client.archivedAt === null)
                    .map((client) => ({ value: client.id, label: client.name })),
                ]}
                /**
                 * ⚠️⚠️ **AN ACTION, NOT AN OPTION — item 21, second pass.** It was the second entry
                 * in the list, which put *create a new client* above most of the clients and, worse,
                 * inside the search: typing a name that does not exist filtered away the one control
                 * that could create it. `Select`'s `action` slot pins it under a divider and exempts
                 * it from the query.
                 */
                {...(onCreateClient === undefined
                  ? {}
                  : {
                      action: {
                        label: t('projects.field.newClient'),
                        onSelect: () => {
                          setNewClientOpen(true);
                        },
                      },
                    })}
              />
            </Field>
            <CheckboxField
              label={t('projects.field.internal')}
              checked={draft.internal}
              onCheckedChange={(internal) => {
                /* Ticking clears the client, so a ticked box can never save one. */
                patch(internal ? { internal: true, clientId: '' } : { internal: false });
              }}
            />
          </div>

          <Field label={t('projects.field.pm')}>
            <Select
              value={draft.pmMemberId}
              /* ⚠️ Item 21 · searchable, for the client picker's reason. */
              searchable
              searchPlaceholder={t('projects.field.pm')}
              onChange={(event) => {
                pmChosen.current = true;
                patch({ pmMemberId: event.target.value });
              }}
              options={[
                { value: '', label: t('common.none') },
                ...pmOptions.map((member) => ({ value: member.id, label: member.name })),
              ]}
            />
          </Field>

          <Field label={t('projects.field.status')}>
            <Select
              value={draft.status}
              onChange={(event) => {
                patch({ status: event.target.value as ProjectStatus });
              }}
              /* 3 Nov 2026 · a NEW project starts as draft or active — the product owner. The
                 server refuses the rest (`PROJECT_CREATE_STATUSES`); offering them would be a
                 choice that can only fail. */
              options={(editing === null ? PROJECT_CREATE_STATUSES : PROJECT_STATUSES).map(
                (status) => ({
                  value: status,
                  label: t(statusKey(status)),
                }),
              )}
            />
          </Field>

          {/*
            ⚠️⚠️ **THE REFERENCE CODE FIELD IS GONE — item 21.** `projects.code` is a free-text
            note — an estimate number, a purchase order — that nothing in this product reads: no
            search, no export column, no URL. It sat between the status and the dates on the one
            dialog somebody fills in under time pressure.

            ⚠️ **The column and the wire field stay** and a project that has one keeps it: the draft
            still carries `code`, so an edit round-trips whatever is stored. What is gone is the
            box. `debt.md` carries the row.
          */}
          <Field label={t('projects.field.start')}>
            <DateField
              label={t('projects.field.start')}
              value={draft.startsOn}
              onChange={(startsOn) => {
                patch({ startsOn });
              }}
            />
          </Field>

          <Field label={t('projects.field.end')}>
            <DateField
              label={t('projects.field.end')}
              value={draft.endsOn}
              onChange={(endsOn) => {
                patch({ endsOn });
              }}
            />
          </Field>
        </div>

        {/* 3 Nov 2026 · a status change carries its reason — asked for here only when the status
            picked differs from the stored one, so an ordinary edit is not slowed by it. */}
        {statusMoves ? (
          <Field required label={t('projects.statusReason.label')}>
            <Textarea
              rows={2}
              maxLength={500}
              value={draft.statusReason}
              placeholder={t('projects.statusReason.placeholder')}
              onChange={(event) => {
                patch({ statusReason: event.target.value });
              }}
            />
          </Field>
        ) : null}

        <ColorPicker
          value={draft.color}
          onChange={(color) => {
            patch({ color });
          }}
          label={t('common.color')}
        />

        {/* P2-32 · ⚠️ **No tracker block.** A project no longer names a tracker project: a line
            item does, in its own *Szinkronizálás* section, and one remote project may feed several
            line items on several projects. This is the form the mockup draws again. */}
        {/* ⚠️ Its own box, below the product's own fields — a workspace's additions come after
            what the product itself defines. Renders nothing when none are defined. */}
        <MetafieldSection
          fields={metafields}
          values={draft.metafields}
          members={members.map((person) => ({ id: person.id, name: person.name }))}
          onChange={(values) => {
            patch({ metafields: values });
          }}
        />
      </div>

      {/**
       * ┌───────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ A CLIENT, WITHOUT LEAVING THE FORM — item 21.                 │
       * └───────────────────────────────────────────────────────────────────┘
       *
       * *"Quick-create client in a modal."* The picker's honest answer is sometimes *"this client
       * does not exist yet"*, and that used to mean abandoning a half-filled project form.
       *
       * ⚠️⚠️ **THE FULL CLIENT FORM, NOT A NAME — 14 Oct 2026, ux-pass-2.** The quick form asked
       * for a name and invented the country and industry, so every client made here had to be
       * corrected on Ügyfelek afterwards. `ClientDialog` is the form Ügyfelek itself uses.
       *
       * ⚠️ **A modal over a modal**: only the outermost surface paints a backdrop, and
       * `useModalSurface` stacks Escape by depth, so closing this leaves the project form standing.
       */}
      <ClientDialog
        open={newClientOpen}
        editing={null}
        existing={clients}
        metafields={clientMetafields}
        serverError={newClientError}
        onClose={() => {
          setNewClientOpen(false);
          setNewClientError(null);
        }}
        onSubmit={(client) => {
          void (async () => {
            if (onCreateClient === undefined) return;
            setNewClientError(null);
            const id = await onCreateClient(client);
            if (id === null) {
              /* ⚠️ Open, with what was typed still in it — a form that vanishes on a refusal
                 loses the work. */
              setNewClientError(t('error.unavailable'));
              return;
            }
            /* Selected on the way back, so the flow ends where it started. */
            patch({ clientId: id });
            setNewClientOpen(false);
          })();
        }}
      />
    </Dialog>
  );
}

function emptyDraft(): ProjectDraft {
  return {
    keyPrefix: '',
    name: '',
    clientId: '',
    internal: false,
    /* The mockup's `newProject` opens on `draft`. */
    status: 'draft',
    statusReason: '',
    pmMemberId: '',
    code: '',
    startsOn: '',
    endsOn: '',
    color: DEFAULT_PROJECT_COLOR,
    metafields: {},
  };
}

/**
 * ⚠️ **A key per status, written out, rather than `t(\`projects.status.${status}\`)`.**
 * `i18n:check` reads literal arguments only — a computed key makes every one of these five report
 * as an orphan, and the fix people reach for is deleting the entry.
 */
export function statusKey(status: ProjectStatus): MessageKey {
  if (status === 'draft') return 'projects.status.draft';
  if (status === 'active') return 'projects.status.active';
  if (status === 'on_hold') return 'projects.status.on_hold';
  if (status === 'completed') return 'projects.status.completed';
  return 'projects.status.archived';
}

/**
 * The mockup's `PSTAT` badge variants, unchanged.
 *
 * `draft: outline`, `active: default`, `on_hold: secondary`, `completed: secondary`,
 * `archived: outline`.
 */
export function statusVariant(status: ProjectStatus): 'default' | 'secondary' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'on_hold' || status === 'completed') return 'secondary';
  return 'outline';
}
